import type { ApolloError } from '@apollo/client/errors';
import type { Application, CreditSettingCalculator } from 'api/core/generated';
import type { AppFileTypes } from 'app-constants';

type ValidateDownPaymentArgs = {
  downPayment: number;
  application: Application;
  creditSetting: CreditSettingCalculator | undefined;
};

export const validateDownPayment = ({
  downPayment,
  application,
  creditSetting,
}: ValidateDownPaymentArgs): boolean => {
  if (!creditSetting) return false;

  const newNetTotal = application.requested_amount - downPayment;

  if (
    downPayment > Number(creditSetting.max_down_payment) ||
    downPayment < Number(creditSetting.min_down_payment) ||
    downPayment < Number(application.credit_info?.min_down_payment) ||
    downPayment < Number(application.credit_info?.min_down_payment) ||
    newNetTotal < Number(application.merchant?.settings?.net_total_min) ||
    newNetTotal > Number(application.merchant?.settings?.net_total_max)
  )
    return false;

  return true;
};

export const extractValidationErrors = (
  errors: ApolloError | Error | boolean | undefined,
): Record<string, boolean> => {
  if (
    typeof errors === 'boolean' ||
    !errors ||
    !('graphQLErrors' in errors) ||
    !errors.graphQLErrors
  ) {
    return {};
  }

  return (errors as ApolloError).graphQLErrors.reduce(
    (acc: Record<string, boolean>, error) => {
      const validation =
        (error.extensions?.validation as Record<string, unknown>) || {};
      const formattedBoolValuesValidationObject = Object.keys(
        validation,
      ).reduce((accObj, keyValue) => ({ ...accObj, [keyValue]: true }), {});

      return { ...acc, ...formattedBoolValuesValidationObject };
    },
    {} as Record<string, boolean>,
  );
};

export const checkUploadFileFormatIsValid = (
  file: File,
  acceptedFileFormats: Array<AppFileTypes>,
): boolean =>
  acceptedFileFormats.some(
    (fileType) =>
      file?.type.includes(fileType) || file?.name.includes(fileType),
  );
